import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

export default function useUpscaleOperation({ endpoint, identity, csrfToken, initialOperation = null, onComplete }) {
  const [operation, setOperation] = useState(initialOperation);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const desiredDecision = useRef(initialOperation?.decision === "accepting" ? "accepted" : initialOperation?.decision === "restoring" ? "restored" : null);
  const complete = useRef(onComplete);
  complete.current = onComplete;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const receive = useCallback((data) => {
    if (!mounted.current) return;
    setOperation(data);
    setError(null);
    if (desiredDecision.current && data.decision === desiredDecision.current) {
      desiredDecision.current = null;
      complete.current?.(data);
    }
  }, []);

  const request = useCallback(async (method, extra = {}, url = endpoint) => {
    setBusy(true);
    setError(null);
    try {
      const payload = { ...identity, ...extra };
      const response = await axios({ method, url, headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {},
        ...(method === "GET" ? { params: payload } : { data: payload }) });
      if (response.data?.id) receive(response.data);
      return response.data;
    } catch (failure) {
      if (mounted.current) setError(failure.response?.data?.error || "Could not connect. Please try again.");
      return null;
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [endpoint, identity, csrfToken, receive]);

  useEffect(() => {
    if (!operation?.id || (!operation.processing && !operation.applying)) return;
    let stopped = false;
    let timer;
    const poll = async () => {
      await request("GET", { operation_id: operation.id });
      if (!stopped) timer = setTimeout(poll, 2000);
    };
    timer = setTimeout(poll, 2000);
    return () => { stopped = true; clearTimeout(timer); };
  }, [operation, request]);

  const start = useCallback((retry = false, options = {}) => request("POST", { ...options, retry_failed: retry }), [request]);
  const accept = useCallback(() => {
    desiredDecision.current = "accepted";
    return request("PATCH", { operation_id: operation.id });
  }, [request, operation?.id]);
  const restore = useCallback((url) => {
    desiredDecision.current = "restored";
    return request("POST", { operation_id: operation.id }, url);
  }, [request, operation?.id]);
  const cancel = useCallback(() => operation?.id ? request("DELETE", { operation_id: operation.id }) : Promise.resolve({}), [request, operation?.id]);
  return { operation, busy, error, start, accept, restore, cancel };
}
